import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import MainLayout from 'core/layout/MainLayout';
import {
    Box,
    Button,
    Container,
    Typography,
    TextField,
    Paper,
    Stack,
    CircularProgress,
    Alert,
    Grid,
    Accordion,
    AccordionSummary,
    AccordionDetails,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import TemplateCard from '../components/TemplateCard';
import { RenderCVHttpService } from '../services';

function RenderCVBuilderPage() {
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [userInfo, setUserInfo] = useState('');
    const [jobInfo, setJobInfo] = useState('');
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [loadingTemplates, setLoadingTemplates] = useState(true);
    const [generating, setGenerating] = useState(false);

    // Load templates on mount
    useEffect(() => {
        const loadTemplates = async () => {
            try {
                const data = await RenderCVHttpService.getTemplates();
                setTemplates(data);
                // Select first template by default
                if (data && data.length > 0) {
                    setSelectedTemplate(data[0].id);
                }
            } catch (error) {
                console.error('Error loading templates:', error);
                enqueueSnackbar('Failed to load templates', { variant: 'error' });
            } finally {
                setLoadingTemplates(false);
            }
        };
        loadTemplates();
    }, [enqueueSnackbar]);

    const handleGenerate = async () => {
        if (!userInfo.trim()) {
            enqueueSnackbar('Please provide information about yourself', { variant: 'warning' });
            return;
        }

        if (!selectedTemplate) {
            enqueueSnackbar('Please select a template', { variant: 'warning' });
            return;
        }

        setGenerating(true);
        try {
            const response = await RenderCVHttpService.generateCV({
                user_info: userInfo.trim(),
                template_id: selectedTemplate,
                job_info: jobInfo.trim() || null,
                title: 'My CV',
            });

            if (response && response.document_id) {
                enqueueSnackbar('CV generated successfully!', { variant: 'success' });
                navigate(`/cv/rendercv/${response.document_id}`);
            } else {
                throw new Error('Invalid response format from server');
            }
        } catch (error) {
            console.error('Error generating CV:', error);
            const errorMessage = error.message || 'Failed to generate CV. Please try again.';
            enqueueSnackbar(errorMessage, { variant: 'error' });
        } finally {
            setGenerating(false);
        }
    };

    return (
        <MainLayout>
            <Container maxWidth="lg" sx={{ mt: 6, mb: 8 }}>
                <Stack spacing={4}>
                    {/* Header */}
                    <Box>
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate('/cv')}
                            sx={{ mb: 2 }}
                        >
                            Back to CVs
                        </Button>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                            <AutoAwesomeIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                            <Box>
                                <Typography variant="h4" fontWeight={700}>
                                    Create CV with RenderCV
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Professional template-based CV builder powered by AI
                                </Typography>
                            </Box>
                        </Box>
                    </Box>

                    {/* Main Content */}
                    <Grid container spacing={3}>
                        {/* Left Column - Form */}
                        <Grid item xs={12} md={6}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 4,
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    height: '100%',
                                }}
                            >
                                <Stack spacing={3}>
                                    <Typography variant="h6" fontWeight={600}>
                                        Tell us about yourself
                                    </Typography>

                                    <TextField
                                        multiline
                                        rows={12}
                                        fullWidth
                                        placeholder="Enter information about yourself, such as:
• Professional summary
• Work experience with job titles, companies, and dates
• Education background, degrees, and institutions
• Skills and certifications
• Languages you speak
• Achievements, awards, or notable projects
• Any other relevant professional information"
                                        value={userInfo}
                                        onChange={(e) => setUserInfo(e.target.value)}
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: 2,
                                            },
                                        }}
                                    />

                                    {/* Job Information Accordion */}
                                    <Accordion>
                                        <AccordionSummary
                                            expandIcon={<ExpandMoreIcon />}
                                            sx={{
                                                '& .MuiAccordionSummary-content': {
                                                    alignItems: 'center',
                                                    gap: 1,
                                                },
                                            }}
                                        >
                                            <WorkOutlineIcon fontSize="small" color="action" />
                                            <Typography variant="body2" color="text.secondary">
                                                Job Information (Optional)
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <TextField
                                                multiline
                                                rows={4}
                                                fullWidth
                                                placeholder="Enter information about the job you're applying for..."
                                                value={jobInfo}
                                                onChange={(e) => setJobInfo(e.target.value)}
                                            />
                                        </AccordionDetails>
                                    </Accordion>

                                    <Button
                                        variant="contained"
                                        size="large"
                                        startIcon={generating ? <CircularProgress size={20} color="inherit" /> : <AutoAwesomeIcon />}
                                        onClick={handleGenerate}
                                        disabled={!userInfo.trim() || !selectedTemplate || generating}
                                        fullWidth
                                        sx={{ mt: 2 }}
                                    >
                                        {generating ? 'Generating...' : 'Generate CV'}
                                    </Button>
                                </Stack>
                            </Paper>
                        </Grid>

                        {/* Right Column - Template Selection */}
                        <Grid item xs={12} md={6}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 4,
                                    borderRadius: 3,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    height: '100%',
                                }}
                            >
                                <Stack spacing={3}>
                                    <Typography variant="h6" fontWeight={600}>
                                        Choose a Template
                                    </Typography>

                                    {loadingTemplates ? (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                            <CircularProgress />
                                        </Box>
                                    ) : templates.length === 0 ? (
                                        <Alert severity="info">No templates available</Alert>
                                    ) : (
                                        <Grid container spacing={2}>
                                            {templates.map((template) => (
                                                <Grid item xs={12} key={template.id}>
                                                    <TemplateCard
                                                        template={template}
                                                        selected={selectedTemplate === template.id}
                                                        onClick={() => setSelectedTemplate(template.id)}
                                                    />
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </Stack>
                            </Paper>
                        </Grid>
                    </Grid>
                </Stack>
            </Container>
        </MainLayout>
    );
}

export default RenderCVBuilderPage;

